import * as THREE from 'three';
import { FLOWERS, type FlowerDef, type FlowerId, type FlowerShape } from '../game/flowers';
import { seededRandom } from '../game/rng';
import { GeometryBuilder, mul, Templates as T, trs, type Vec3 } from './GeometryBuilder';

/** Visual growth stages derived from crop growth. */
export const Stage = { Seed: 0, Sprout: 1, Bud: 2, Bloom: 3 } as const;
export type Stage = (typeof Stage)[keyof typeof Stage];

export function growthStage(growth: number): Stage {
  if (growth >= 1) return Stage.Bloom;
  if (growth >= 0.5) return Stage.Bud;
  if (growth >= 0.12) return Stage.Sprout;
  return Stage.Seed;
}

/** 0..1 progress within the current stage (used for smooth scaling). */
export function stageProgress(growth: number): number {
  if (growth >= 1) return 1;
  if (growth >= 0.5) return (growth - 0.5) / 0.5;
  if (growth >= 0.12) return (growth - 0.12) / 0.38;
  return growth / 0.12;
}

const STEM = '#5fae4a';
const LEAF = '#58b548';
const LEAF_TIP = '#8fdc6c';
const SOIL_MOUND = '#6e4a30';

/** Low-poly petal template (cheap: these are used hundreds of times). */
const PETAL = new THREE.SphereGeometry(1, 6, 4);

const CLUSTER_SIZE: Record<FlowerShape, number> = {
  daisy: 5,
  tulip: 4,
  sunflower: 2,
  rose: 3,
  lavender: 6,
  lily: 3,
  orchid: 2,
  lotus: 1,
  bell: 3,
  star: 3,
};

const STEM_HEIGHT: Record<FlowerShape, number> = {
  daisy: 0.42,
  tulip: 0.48,
  sunflower: 1.15,
  rose: 0.5,
  lavender: 0.65,
  lily: 0.55,
  orchid: 0.45,
  lotus: 0.1,
  bell: 0.5,
  star: 0.45,
};

/** Overall plant size relative to the model units above. */
const PLANT_SCALE = 1.7;

const LAYOUTS: Record<number, ReadonlyArray<readonly [number, number]>> = {
  1: [[0, 0]],
  2: [
    [-0.32, -0.22],
    [0.34, 0.26],
  ],
  3: [
    [-0.4, -0.3],
    [0.42, -0.22],
    [0.02, 0.4],
  ],
  4: [
    [-0.4, -0.38],
    [0.4, -0.4],
    [-0.38, 0.4],
    [0.42, 0.38],
  ],
  5: [
    [-0.45, -0.42],
    [0.45, -0.45],
    [-0.43, 0.45],
    [0.46, 0.42],
    [0.02, 0.0],
  ],
  6: [
    [-0.5, -0.4],
    [0.0, -0.5],
    [0.5, -0.38],
    [-0.48, 0.38],
    [0.04, 0.3],
    [0.52, 0.46],
  ],
};

interface Plant {
  b: GeometryBuilder;
  base: THREE.Matrix4;
  def: FlowerDef;
  rand: () => number;
  scale: number;
}

const colorAt = (def: FlowerDef, i: number): string => def.colors[i % def.colors.length] ?? '#fff';

function stem(p: Plant, height: number, radius = 0.022, color = STEM): void {
  p.b.add(T.cylinder, color, mul(p.base, trs([0, 0, 0], [0, 0, 0], [radius, height, radius])));
}

/** A leaf sprouting from the stem at height `y`, rising by `rise` radians. */
function leaf(
  p: Plant,
  y: number,
  yaw: number,
  length: number,
  width: number,
  rise = 0.45,
  color = LEAF,
) {
  p.b.add(
    PETAL,
    color,
    mul(
      p.base,
      trs([0, y, 0], [0, yaw, 0]),
      trs([0, 0, 0], [0, 0, rise]),
      trs([length, 0, 0], [0, 0, 0], [length, length * 0.14, width]),
    ),
    { color2: color === LEAF ? LEAF_TIP : color, axis: 'x' },
  );
}

/** Flower head frame at the top of the stem, tilted towards +z (the default camera). */
function headFrame(p: Plant, height: number, tilt: number, yaw: number): THREE.Matrix4 {
  return mul(
    p.base,
    trs([0, height, 0]),
    trs([0, 0, 0], [0, yaw, 0]),
    trs([0, 0, 0], [tilt, 0, 0]),
  );
}

interface RingOptions {
  count: number;
  radius: number;
  /** Positive values lift petal tips upwards (cupping). */
  tilt: number;
  /** Petal half-extents: [length, thickness, width]. */
  size: Vec3;
  offset?: number;
  y?: number;
  colors: (i: number) => readonly [string, string?];
  glow?: boolean;
  template?: THREE.BufferGeometry;
}

function petalRing(p: Plant, head: THREE.Matrix4, o: RingOptions): void {
  for (let i = 0; i < o.count; i++) {
    const angle = (o.offset ?? 0) + (i / o.count) * Math.PI * 2;
    const [c1, c2] = o.colors(i);
    p.b.add(
      o.template ?? PETAL,
      c1,
      mul(
        head,
        trs([0, o.y ?? 0, 0], [0, angle, 0]),
        trs([o.radius, 0, 0], [0, 0, o.tilt]),
        trs([o.size[0], 0, 0], [0, 0, 0], o.size),
      ),
      { color2: c2 ?? c1, axis: 'x', glow: o.glow },
    );
  }
}

// ---------------------------------------------------------------------------
// Early stages (shared by all species)
// ---------------------------------------------------------------------------

function buildSeed(p: Plant): void {
  p.b.add(T.sphere, SOIL_MOUND, mul(p.base, trs([0, 0, 0], [0, 0, 0], [0.13, 0.05, 0.13])));
  p.b.add(
    PETAL,
    '#e9d8a6',
    mul(p.base, trs([0.02, 0.045, 0.01], [0.4, 0.2, 0], [0.025, 0.018, 0.018])),
  );
}

function buildSprout(p: Plant): void {
  stem(p, 0.14, 0.015);
  for (const side of [0, Math.PI]) {
    p.b.add(
      PETAL,
      LEAF,
      mul(
        p.base,
        trs([0, 0.14, 0], [0, side + p.rand() * 0.4, 0]),
        trs([0, 0, 0], [0, 0, 0.45]),
        trs([0.045, 0, 0], [0, 0, 0], [0.045, 0.012, 0.032]),
      ),
      { color2: LEAF_TIP, axis: 'x' },
    );
  }
}

function buildBud(p: Plant): void {
  const { def } = p;
  const h = STEM_HEIGHT[def.shape] * 0.8;
  if (def.shape === 'lotus') {
    lotusPads(p);
    stem(p, 0.12, 0.02);
    p.b.add(T.sphere, LEAF, mul(p.base, trs([0, 0.16, 0], [0, 0, 0], [0.05, 0.08, 0.05])), {
      color2: colorAt(def, 1),
    });
    return;
  }
  stem(p, h, def.shape === 'sunflower' ? 0.032 : 0.02);
  leaf(p, h * 0.25, p.rand() * 6.28, 0.09, 0.04);
  leaf(p, h * 0.35, p.rand() * 6.28 + 3, 0.08, 0.035);
  if (def.shape === 'lavender') {
    for (let k = 0; k < 6; k++) {
      p.b.add(
        PETAL,
        '#9bbf7a',
        mul(p.base, trs([0, h + k * 0.035, 0], [0, k, 0], [0.02, 0.025, 0.02])),
        {
          color2: colorAt(def, 0),
        },
      );
    }
    return;
  }
  const head = headFrame(p, h, 0.15, 0);
  if (def.shape === 'sunflower') {
    p.b.add(T.sphere, LEAF, mul(head, trs([0, 0.02, 0], [0, 0, 0], [0.09, 0.05, 0.09])));
    petalRing(p, head, {
      count: 10,
      radius: 0.06,
      tilt: 1.1,
      size: [0.03, 0.01, 0.015],
      y: 0.02,
      colors: () => [LEAF, colorAt(def, 0)],
    });
    return;
  }
  p.b.add(T.sphere, LEAF, mul(head, trs([0, 0.05, 0], [0, 0, 0], [0.045, 0.075, 0.045])), {
    color2: colorAt(def, 0),
    glow: false,
  });
}

// ---------------------------------------------------------------------------
// Full blooms
// ---------------------------------------------------------------------------

function lotusPads(p: Plant): void {
  for (const [x, z, r] of [
    [0.32, 0.18, 0.3],
    [-0.3, -0.24, 0.26],
    [-0.15, 0.36, 0.2],
  ] as const) {
    p.b.add(T.cylinder, '#4f9e58', mul(p.base, trs([x, 0, z], [0, 0, 0], [r, 0.02, r])), {
      color2: '#7cc56f',
    });
  }
}

const BLOOMS: Record<FlowerShape, (p: Plant) => void> = {
  daisy(p) {
    const h = STEM_HEIGHT.daisy;
    stem(p, h);
    leaf(p, 0.08, p.rand() * 6.28, 0.11, 0.045);
    leaf(p, 0.12, p.rand() * 6.28, 0.1, 0.04);
    const head = headFrame(p, h, 0.55 + p.rand() * 0.3, p.rand() * 1.2 - 0.6);
    p.b.add(T.sphere, p.def.center, mul(head, trs([0, 0.012, 0], [0, 0, 0], [0.05, 0.03, 0.05])));
    petalRing(p, head, {
      count: 12,
      radius: 0.03,
      tilt: 0.15,
      size: [0.06, 0.012, 0.024],
      colors: () => ['#fff7e8', colorAt(p.def, 0)],
    });
  },

  tulip(p) {
    const h = STEM_HEIGHT.tulip;
    stem(p, h);
    leaf(p, 0.05, p.rand() * 6.28, 0.16, 0.05, 1.1);
    leaf(p, 0.07, p.rand() * 6.28, 0.14, 0.045, 1.2);
    const head = headFrame(p, h, p.rand() * 0.2, 0);
    const n = p.def.colors.length;
    petalRing(p, head, {
      count: 3,
      radius: 0.022,
      tilt: 1.22,
      size: [0.08, 0.026, 0.045],
      colors: (i) => (n > 2 ? [colorAt(p.def, i * 2)] : [colorAt(p.def, 0), colorAt(p.def, 1)]),
    });
    petalRing(p, head, {
      count: 3,
      radius: 0.034,
      tilt: 1.12,
      offset: Math.PI / 3,
      size: [0.082, 0.026, 0.046],
      colors: (i) => (n > 2 ? [colorAt(p.def, i * 2 + 1)] : [colorAt(p.def, 0), colorAt(p.def, 1)]),
    });
  },

  sunflower(p) {
    const h = STEM_HEIGHT.sunflower;
    stem(p, h, 0.034);
    leaf(p, 0.3, p.rand() * 6.28, 0.15, 0.1, 0.25);
    leaf(p, 0.55, p.rand() * 6.28, 0.14, 0.09, 0.3);
    leaf(p, 0.78, p.rand() * 6.28, 0.12, 0.08, 0.35);
    const head = headFrame(p, h, 1.0 + p.rand() * 0.15, p.rand() * 0.6 - 0.3);
    p.b.add(T.sphere, LEAF, mul(head, trs([0, -0.035, 0], [0, 0, 0], [0.12, 0.03, 0.12])));
    petalRing(p, head, {
      count: 14,
      radius: 0.1,
      tilt: -0.05,
      size: [0.07, 0.012, 0.03],
      colors: () => [colorAt(p.def, 1)],
    });
    petalRing(p, head, {
      count: 14,
      radius: 0.095,
      tilt: 0.12,
      y: 0.01,
      offset: Math.PI / 14,
      size: [0.065, 0.014, 0.028],
      colors: () => [colorAt(p.def, 0)],
    });
    p.b.add(T.cylinder, '#5a3418', mul(head, trs([0, -0.01, 0], [0, 0, 0], [0.125, 0.035, 0.125])));
    p.b.add(
      T.sphere,
      p.def.center,
      mul(head, trs([0, 0.025, 0], [0, 0, 0], [0.105, 0.025, 0.105])),
    );
  },

  rose(p) {
    const h = STEM_HEIGHT.rose;
    stem(p, h, 0.02, '#4f9a3f');
    leaf(p, 0.18, p.rand() * 6.28, 0.07, 0.04, 0.3, '#3f8f3c');
    leaf(p, 0.3, p.rand() * 6.28, 0.065, 0.035, 0.3, '#3f8f3c');
    const head = headFrame(p, h, 0.35, p.rand() * 1.0 - 0.5);
    petalRing(p, head, {
      count: 5,
      radius: 0.02,
      tilt: -0.4,
      y: -0.01,
      size: [0.035, 0.008, 0.018],
      colors: () => ['#3f8f3c'],
    });
    const [c0, c1] = [colorAt(p.def, 0), colorAt(p.def, 1)];
    p.b.add(T.sphere, p.def.center, mul(head, trs([0, 0.05, 0], [0, 0, 0], [0.032, 0.048, 0.032])));
    petalRing(p, head, {
      count: 4,
      radius: 0.028,
      tilt: 1.35,
      size: [0.05, 0.022, 0.045],
      colors: () => [c0, p.def.center],
    });
    petalRing(p, head, {
      count: 5,
      radius: 0.044,
      tilt: 1.0,
      offset: 0.6,
      size: [0.055, 0.02, 0.05],
      colors: () => [c0, c1],
    });
    petalRing(p, head, {
      count: 6,
      radius: 0.052,
      tilt: 0.55,
      offset: 0.3,
      y: -0.01,
      size: [0.055, 0.018, 0.055],
      colors: () => [c0, c1],
    });
  },

  lavender(p) {
    const h = STEM_HEIGHT.lavender + p.rand() * 0.12;
    stem(p, h, 0.012, '#7fa36b');
    for (let i = 0; i < 3; i++) leaf(p, 0.02, i * 2.1 + p.rand(), 0.12, 0.014, 1.15, '#86a879');
    const levels = 10;
    for (let k = 0; k < levels; k++) {
      const t = k / levels;
      const y = h * 0.55 + t * h * 0.45;
      const r = 0.026 - t * 0.012;
      for (let j = 0; j < 2; j++) {
        const angle = k * 1.3 + j * Math.PI;
        p.b.add(
          PETAL,
          colorAt(p.def, 0),
          mul(
            p.base,
            trs(
              [Math.cos(angle) * 0.016, y, Math.sin(angle) * 0.016],
              [0, angle, 0],
              [r, r * 1.2, r],
            ),
          ),
          { color2: colorAt(p.def, 1) },
        );
      }
    }
  },

  lily(p) {
    const h = STEM_HEIGHT.lily;
    stem(p, h);
    leaf(p, 0.1, p.rand() * 6.28, 0.15, 0.03, 0.8);
    leaf(p, 0.18, p.rand() * 6.28, 0.13, 0.028, 0.7);
    const head = headFrame(p, h, 0.6, p.rand() * 1.2 - 0.6);
    const colors = () => [colorAt(p.def, 1), colorAt(p.def, 0)] as const;
    petalRing(p, head, { count: 3, radius: 0.015, tilt: 0.8, size: [0.1, 0.012, 0.036], colors });
    petalRing(p, head, {
      count: 3,
      radius: 0.015,
      tilt: 0.62,
      offset: Math.PI / 3,
      size: [0.105, 0.012, 0.03],
      colors,
    });
    for (let i = 0; i < 3; i++) {
      const a = (i / 3) * Math.PI * 2;
      const m = mul(
        head,
        trs(
          [Math.cos(a) * 0.012, 0, Math.sin(a) * 0.012],
          [Math.sin(a) * 0.3, 0, -Math.cos(a) * 0.3],
        ),
      );
      p.b.add(T.cylinder, '#d9f2b4', mul(m, trs([0, 0, 0], [0, 0, 0], [0.004, 0.08, 0.004])));
      p.b.add(T.sphere, p.def.center, mul(m, trs([0, 0.085, 0], [0, 0, 0], [0.012, 0.008, 0.012])));
    }
  },

  orchid(p) {
    for (const yaw of [0.4, 2.6]) leaf(p, 0.02, yaw + p.rand() * 0.4, 0.17, 0.07, 0.15, '#3f8f45');
    // Gently arching stem built from three tilted segments.
    let m = p.base.clone();
    const segments: ReadonlyArray<readonly [number, number]> = [
      [0.22, 0],
      [0.16, 0.45],
      [0.14, 0.5],
    ];
    const bloomPoints: THREE.Vector3[] = [];
    for (const [length, bend] of segments) {
      m = mul(m, trs([0, 0, 0], [bend, 0, 0]));
      p.b.add(T.cylinder, STEM, mul(m, trs([0, 0, 0], [0, 0, 0], [0.012, length, 0.012])));
      m = mul(m, trs([0, length, 0]));
      bloomPoints.push(new THREE.Vector3().setFromMatrixPosition(m));
    }
    for (const point of bloomPoints.slice(1)) {
      const head = trs([point.x, point.y, point.z], [1.25, p.rand() * 0.6 - 0.3, 0], p.scale);
      const [c0, c1] = [colorAt(p.def, 0), colorAt(p.def, 1)];
      for (const a of [Math.PI / 2, (7 * Math.PI) / 6, (11 * Math.PI) / 6]) {
        petalRing(p, head, {
          count: 1,
          radius: 0.008,
          tilt: 0.1,
          offset: a,
          size: [0.06, 0.008, 0.02],
          colors: () => [c0, c1],
        });
      }
      for (const a of [Math.PI / 6, (5 * Math.PI) / 6]) {
        petalRing(p, head, {
          count: 1,
          radius: 0.008,
          tilt: 0.15,
          offset: a,
          size: [0.055, 0.008, 0.042],
          colors: () => [c1, c0],
        });
      }
      p.b.add(
        PETAL,
        '#a8329c',
        mul(head, trs([0, 0.02, 0.035], [0.3, 0, 0], [0.032, 0.022, 0.03])),
      );
      p.b.add(
        T.sphere,
        p.def.center,
        mul(head, trs([0, 0.015, 0], [0, 0, 0], [0.012, 0.012, 0.012])),
      );
    }
  },

  lotus(p) {
    lotusPads(p);
    stem(p, STEM_HEIGHT.lotus, 0.02);
    const head = headFrame(p, STEM_HEIGHT.lotus, 0, 0);
    const glow = p.def.glow !== undefined;
    const many = p.def.colors.length > 2;
    const colors = (offset: number) => (i: number) =>
      many
        ? ([colorAt(p.def, i + offset), colorAt(p.def, i + offset + 1)] as const)
        : ([colorAt(p.def, 0), colorAt(p.def, 1)] as const);
    petalRing(p, head, {
      count: 8,
      radius: 0.04,
      tilt: 0.42,
      size: [0.09, 0.016, 0.045],
      colors: colors(0),
      glow,
    });
    petalRing(p, head, {
      count: 8,
      radius: 0.03,
      tilt: 0.85,
      offset: Math.PI / 8,
      y: 0.01,
      size: [0.085, 0.016, 0.04],
      colors: colors(1),
      glow,
    });
    petalRing(p, head, {
      count: 6,
      radius: 0.02,
      tilt: 1.2,
      y: 0.02,
      size: [0.07, 0.016, 0.035],
      colors: colors(2),
      glow,
    });
    p.b.add(
      T.cylinder,
      p.def.center,
      mul(head, trs([0, 0.02, 0], [0, 0, 0], [0.035, 0.035, 0.035])),
      { glow },
    );
  },

  bell(p) {
    const h = STEM_HEIGHT.bell;
    stem(p, h, 0.016);
    leaf(p, 0.06, p.rand() * 6.28, 0.14, 0.06, 0.4);
    leaf(p, 0.1, p.rand() * 6.28, 0.12, 0.05, 0.5);
    const yaw = p.rand() * 1.2 - 0.6;
    const arch = mul(p.base, trs([0, h, 0], [0, yaw, 0]), trs([0, 0, 0], [1.1, 0, 0]));
    p.b.add(T.cylinder, STEM, mul(arch, trs([0, 0, 0], [0, 0, 0], [0.012, 0.22, 0.012])));
    for (const t of [0.07, 0.14, 0.21]) {
      const top = new THREE.Vector3().setFromMatrixPosition(mul(arch, trs([0, t, 0])));
      p.b.add(
        T.cylinder,
        STEM,
        trs(
          [top.x, top.y - 0.04 * p.scale, top.z],
          [0, 0, 0],
          [0.006 * p.scale, 0.04 * p.scale, 0.006 * p.scale],
        ),
      );
      p.b.add(
        T.bell,
        colorAt(p.def, 0),
        trs([top.x, top.y - 0.04 * p.scale, top.z], [0, 0, 0], 0.055 * p.scale),
        {
          color2: colorAt(p.def, 1),
          axis: 'y',
          glow: true,
        },
      );
    }
  },

  star(p) {
    const h = STEM_HEIGHT.star;
    stem(p, h);
    leaf(p, 0.08, p.rand() * 6.28, 0.11, 0.045);
    leaf(p, 0.14, p.rand() * 6.28, 0.1, 0.04);
    const head = headFrame(p, h, 0.75, p.rand() * 1.0 - 0.5);
    p.b.add(T.star, colorAt(p.def, 0), mul(head, trs([0, 0, 0], [0, p.rand() * 6.28, 0], 0.09)), {
      color2: colorAt(p.def, 1),
      axis: 'z',
      glow: true,
    });
    p.b.add(T.sphere, p.def.center, mul(head, trs([0, 0.02, 0], [0, 0, 0], 0.028)), { glow: true });
  },
};

// ---------------------------------------------------------------------------
// Cluster assembly + cache
// ---------------------------------------------------------------------------

export interface FlowerGeometry {
  base: THREE.BufferGeometry;
  glow: THREE.BufferGeometry | null;
}

const cache = new Map<string, FlowerGeometry>();

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** Geometry for one plot's worth of a species at a growth stage (cached). */
export function flowerGeometry(species: FlowerId, stage: Stage): FlowerGeometry {
  const key = `${species}:${stage}`;
  const cached = cache.get(key);
  if (cached) return cached;

  const def = FLOWERS[species];
  const rand = seededRandom(hashString(key));
  const b = new GeometryBuilder();
  const layout = LAYOUTS[CLUSTER_SIZE[def.shape]] ?? LAYOUTS[1]!;

  for (const [x, z] of layout) {
    const scale = (0.88 + rand() * 0.24) * PLANT_SCALE;
    const jitter = (): number => (rand() - 0.5) * 0.12;
    const base = trs(
      [x + jitter(), 0, z + jitter()],
      [(rand() - 0.5) * 0.16, 0, (rand() - 0.5) * 0.16],
      scale,
    );
    const plant: Plant = { b, base, def, rand, scale };
    if (stage === Stage.Seed) buildSeed(plant);
    else if (stage === Stage.Sprout) buildSprout(plant);
    else if (stage === Stage.Bud) buildBud(plant);
    else BLOOMS[def.shape](plant);
  }

  const built = b.build();
  cache.set(key, built);
  return built;
}
